import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Col, Row } from '@/lib/utils';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import SvgIcon from '@/components/SvgIcon';
import {
    YearMonth,
    ZeroableInRateCoach,
    addMonths,
    describeNoshowAlwaysInChange,
    formatYearMonth,
    loadZeroableInRateCoaches,
    parseYearMonth,
    toYearMonthString,
    useSaveNoshowAlwaysIn,
    zeroableReasonLabel,
} from '@/features/noshowAlwaysIn';
import { getSettlementClassificationErrorMessage } from '@/features/settlementClassification';
import DialogShell from './exceptionRates/DialogShell';

type Props = {
    centerId: string;
    /** 서버 값('YYYY-MM' | null). 요율표 조회 응답의 noshowAlwaysInFromMonth. */
    fromMonth: string | null;
    readOnly: boolean;
    /** 저장 성공 — 화면 표시를 응답값으로 바로 고친다(요율 목록은 다시 읽지 않는다). */
    onSaved: (fromMonth: string | null) => void;
};

const thisMonth = (): YearMonth => ({ year: dayjs().year(), month: dayjs().month() + 1 });

const secondaryButton =
    'h-[40px] px-[18px] rounded-[9px] bg-[#F4F5F7] flex items-center justify-center cursor-pointer';
const primaryButton = 'h-[40px] px-[18px] rounded-[9px] bg-primary flex items-center justify-center';

/**
 * 상세 정산 탭 위의 센터 설정 한 줄 — 「노쇼 수업은 항상 IN」(서버 noshowAlwaysInFromMonth).
 *
 * - 꺼짐/켜짐(시작 달)을 보이고, 「설정」에서 시작 달을 고르거나 끈다. 기본 시작 달은 이번 달.
 * - 저장 전에 항상 확인을 받는다: 지난 달을 고르면 그 달~지난달 급여가 다시 계산되고, 끄면 켜져 있던 모든 달이 원래대로 돌아간다.
 * - 켤 때는 그 시작 달 요율표에서 IN 요율이 0원이 될 수 있는 강사를 확인 창에 보인다(서버 판정 그대로).
 *   저장 응답의 목록에 확인 창에 없던 강사가 있으면 저장 뒤 한 번 더 알린다.
 * - 금액은 다루지 않는다. 계산은 서버가 한다.
 */
const NoshowAlwaysInSetting = ({ centerId, fromMonth, readOnly, onSaved }: Props) => {
    const now = thisMonth();
    const current = parseYearMonth(fromMonth);
    const [open, setOpen] = useState(false);

    return (
        <>
            <Row
                aria-label="노쇼 수업은 항상 IN"
                className="items-center justify-between mt-[10px] px-[16px] py-[12px] rounded-[10px] bg-[#F6F8FA] gap-[12px]"
            >
                <Row className="items-center gap-[10px] min-w-0">
                    <MediumText fontSize={15} color="#3D4244" className="whitespace-nowrap">
                        노쇼 수업은 항상 IN
                    </MediumText>
                    {current ? (
                        <span className="shrink-0 rounded-full bg-[#E6F2EC] px-[10px] py-[2px] text-[13px] leading-[20px] text-[#12A25D]">
                            {`${current.year}년 ${current.month}월부터 켜짐`}
                        </span>
                    ) : (
                        <span className="shrink-0 rounded-full bg-[#E6E8EB] px-[10px] py-[2px] text-[13px] leading-[20px] text-[#6B7280]">
                            꺼짐
                        </span>
                    )}
                    <span className="truncate">
                        <DemiLightText fontSize={13} color="#6B7280">
                            노쇼·당일 취소 수업(개인·그룹)을 근무 시간과 상관없이 IN 요율로 계산해요.
                        </DemiLightText>
                    </span>
                </Row>
                {!readOnly && (
                    <div
                        role="button"
                        className="shrink-0 h-[32px] px-[14px] rounded-[8px] border border-solid border-[#D9D9D9] bg-white flex items-center cursor-pointer hover:bg-[#F2F4F6]"
                        onClick={() => setOpen(true)}
                    >
                        <RegularText fontSize={14} color="#3D73DD">
                            설정
                        </RegularText>
                    </div>
                )}
            </Row>
            {open && (
                <NoshowAlwaysInDialog
                    centerId={centerId}
                    fromMonth={fromMonth}
                    now={now}
                    onClose={() => setOpen(false)}
                    onSaved={onSaved}
                />
            )}
        </>
    );
};

type Step =
    | { kind: 'edit' }
    | { kind: 'confirm'; after: string | null; zeroable: ZeroableInRateCoach[] | null }
    | { kind: 'done'; zeroable: ZeroableInRateCoach[] };

export const NoshowAlwaysInDialog = ({
    centerId,
    fromMonth,
    now,
    onClose,
    onSaved,
}: {
    centerId: string;
    fromMonth: string | null;
    now: YearMonth;
    onClose: () => void;
    onSaved: (fromMonth: string | null) => void;
}) => {
    const current = parseYearMonth(fromMonth);
    const [picked, setPicked] = useState<YearMonth>(current ?? now);
    const [step, setStep] = useState<Step>({ kind: 'edit' });
    const [checking, setChecking] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const save = useSaveNoshowAlwaysIn();
    const busy = save.isLoading || checking;
    const pickedValue = toYearMonthString(picked);
    const unchanged = !!current && pickedValue === fromMonth;

    // 달을 다시 고르면 앞 저장 실패 문구를 지운다.
    useEffect(() => setError(null), [picked]);

    const confirm = useMemo(
        () => (step.kind === 'confirm' ? describeNoshowAlwaysInChange(fromMonth, step.after, now) : null),
        [step, fromMonth, now],
    );

    const askTurnOn = async () => {
        if (busy || unchanged) return;
        setChecking(true);
        const zeroable = await loadZeroableInRateCoaches(centerId, picked);
        setChecking(false);
        setStep({ kind: 'confirm', after: pickedValue, zeroable });
    };

    const submit = async (after: string | null, shown: ZeroableInRateCoach[] | null) => {
        try {
            const res = await save.mutateAsync({ centerId, fromMonth: after });
            const saved = res?.noshowAlwaysInFromMonth ?? after;
            onSaved(saved);
            const shownIds = new Set((shown ?? []).map((coach) => coach.coachId));
            const missed = (res?.coachesWithZeroableInRate ?? []).filter((coach) => !shownIds.has(coach.coachId));
            if (after && missed.length > 0) {
                setStep({ kind: 'done', zeroable: res?.coachesWithZeroableInRate ?? missed });
                return;
            }
            onClose();
        } catch (e) {
            setStep({ kind: 'edit' });
            setError(getSettlementClassificationErrorMessage(e as any));
        }
    };

    if (step.kind === 'done') {
        return (
            <DialogShell title="IN 요율을 확인해 주세요" width={460} onClose={onClose}>
                <Col className="px-[24px] pb-[22px]">
                    <RegularText fontSize={15} lineHeight={23} color="#3D4244" className="mt-[10px] break-keep">
                        저장했어요. 아래 강사는 매출 구간에 따라 IN 요율이 0일 수 있어요. 그 구간에서는 IN 요율로 계산하는 노쇼 수업이 0원이 돼요(개별 요율·예외 금액 수업은 그대로).
                    </RegularText>
                    <ZeroableList coaches={step.zeroable} />
                    <Row className="justify-end mt-[20px]">
                        <div className={`${primaryButton} cursor-pointer`} onClick={onClose}>
                            <RegularText fontSize={15} color="white">
                                확인
                            </RegularText>
                        </div>
                    </Row>
                </Col>
            </DialogShell>
        );
    }

    if (step.kind === 'confirm' && confirm) {
        const turningOff = step.after === null;
        return (
            <DialogShell
                title={confirm.title}
                width={460}
                onClose={() => !busy && setStep({ kind: 'edit' })}
                busy={busy}
            >
                <Col className="px-[24px] pb-[22px]">
                    {confirm.lines.map((line) => (
                        <RegularText key={line} fontSize={15} lineHeight={23} color="#3D4244" className="mt-[10px] break-keep">
                            {line}
                        </RegularText>
                    ))}
                    {!turningOff && step.zeroable && step.zeroable.length > 0 && (
                        <Col className="mt-[12px] rounded-[8px] bg-[#FFF6E5] px-[12px] py-[10px]">
                            <RegularText fontSize={14} lineHeight={20} color="#8A5A00" className="break-keep">
                                아래 강사는 매출 구간에 따라 IN 요율이 0일 수 있어요. 그 구간에서는 IN 요율로 계산하는 노쇼 수업이 0원이 돼요(개별 요율·예외 금액 수업은 그대로).
                            </RegularText>
                            <ZeroableList coaches={step.zeroable} />
                        </Col>
                    )}
                    {!turningOff && step.zeroable === null && (
                        <DemiLightText fontSize={13} lineHeight={20} color="#6B7280" className="mt-[10px] break-keep">
                            강사별 IN 요율을 확인하지 못했어요. 저장 뒤 0원이 될 수 있는 강사가 있으면 알려 드려요.
                        </DemiLightText>
                    )}
                    <Row className="justify-end gap-[8px] mt-[20px]">
                        <div className={secondaryButton} onClick={busy ? undefined : () => setStep({ kind: 'edit' })}>
                            <RegularText fontSize={15} color="#3D4244">
                                취소
                            </RegularText>
                        </div>
                        <div
                            className={`${primaryButton} ${busy ? 'bg-opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                            onClick={busy ? undefined : () => submit(step.after, step.zeroable)}
                        >
                            <RegularText fontSize={15} color="white">
                                {save.isLoading ? '저장 중…' : turningOff ? '끄기' : current ? '바꾸기' : '켜기'}
                            </RegularText>
                        </div>
                    </Row>
                </Col>
            </DialogShell>
        );
    }

    return (
        <DialogShell title="노쇼 수업은 항상 IN" width={480} onClose={onClose} busy={busy}>
            <Col className="px-[24px] pb-[22px]">
                <RegularText fontSize={14} lineHeight={21} color="#6B7280" className="mt-[6px] break-keep">
                    노쇼·당일 취소 수업(개인·그룹)은 근무 시간과 상관없이 IN 요율로 계산해요. 출석한 수업은 지금처럼 근무
                    시간으로 나눠요.
                </RegularText>

                <Row className="items-center justify-between mt-[20px]">
                    <MediumText fontSize={15} color="#3D4244">
                        시작 달
                    </MediumText>
                    <Row className="items-center gap-[6px]">
                        <div
                            role="button"
                            aria-label="이전 달"
                            className="p-[8px] cursor-pointer"
                            onClick={busy ? undefined : () => setPicked((prev) => addMonths(prev, -1))}
                        >
                            <SvgIcon name="SvgLeftStrokeArrow" size={14} fill="transparent" stroke="#808B96" strokeWidth={2} />
                        </div>
                        <MediumText fontSize={16} color="#181D24" className="w-[110px] text-center">
                            {`${picked.year}년 ${picked.month}월`}
                        </MediumText>
                        <div
                            role="button"
                            aria-label="다음 달"
                            className="p-[8px] cursor-pointer"
                            onClick={busy ? undefined : () => setPicked((prev) => addMonths(prev, 1))}
                        >
                            <SvgIcon name="SvgRightStrokeArrow" size={14} fill="transparent" stroke="#808B96" strokeWidth={2} />
                        </div>
                    </Row>
                </Row>
                <DemiLightText fontSize={13} lineHeight={19} color="#6B7280" className="mt-[4px] self-end">
                    {picked.year * 12 + picked.month < now.year * 12 + now.month
                        ? `${formatYearMonth(picked, now)}부터 지난달까지 급여도 다시 계산돼요.`
                        : `${formatYearMonth(picked, now)} 수업부터 적용돼요.`}
                </DemiLightText>

                {error && (
                    <RegularText fontSize={14} color="#D93043" className="mt-[12px]">
                        {error}
                    </RegularText>
                )}

                <Row className="items-center justify-between mt-[22px]">
                    {current ? (
                        <RegularText
                            fontSize={14}
                            color="#D93043"
                            className={busy ? 'cursor-not-allowed' : 'cursor-pointer underline underline-offset-4'}
                            onClick={busy ? undefined : () => setStep({ kind: 'confirm', after: null, zeroable: null })}
                        >
                            끄기
                        </RegularText>
                    ) : (
                        <div />
                    )}
                    <Row className="gap-[8px]">
                        <div className={secondaryButton} onClick={busy ? undefined : onClose}>
                            <RegularText fontSize={15} color="#3D4244">
                                닫기
                            </RegularText>
                        </div>
                        <div
                            className={`${primaryButton} ${busy || unchanged ? 'bg-opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                            onClick={busy || unchanged ? undefined : askTurnOn}
                        >
                            <RegularText fontSize={15} color="white">
                                {checking ? '확인 중…' : current ? '시작 달 바꾸기' : '켜기'}
                            </RegularText>
                        </div>
                    </Row>
                </Row>
            </Col>
        </DialogShell>
    );
};

const ZeroableList = ({ coaches }: { coaches: ZeroableInRateCoach[] }) => (
    <Col className="mt-[6px] max-h-[160px] overflow-y-auto">
        {coaches.map((coach) => (
            <RegularText key={coach.coachId} fontSize={14} lineHeight={21} color="#3D4244">
                {`· ${coach.name || '이름 없음'} (${zeroableReasonLabel(coach.reason)})`}
            </RegularText>
        ))}
    </Col>
);

export default NoshowAlwaysInSetting;
